import type {
  FramingGeometry,
  LngLat,
  Shape,
} from "../adapters/types";
import {
  lngLatToMercator,
  mercatorToLngLat,
} from "../loaders/formats/shared";

export type MercatorPoint = [number, number];

export type FramingMargin = "tight" | "normal" | "wide";

export interface FramingRect {
  center: MercatorPoint;
  width: number;
  height: number;
}

export interface MercatorBox {
  min: MercatorPoint;
  max: MercatorPoint;
}

export type FramingStart = { mode: "content" } | { mode: "frame"; frame: FramingRect };

export type FramingLimit =
  | { mode: "none" }
  | { mode: "content"; margin: FramingMargin }
  | { mode: "frame"; frame: FramingRect };

export interface MapFraming {
  start: FramingStart;
  rotationDeg: number;
  pitchDeg: number;
  limit: FramingLimit;
  maxZoomFactor: number;
}

export interface Viewport {
  width: number;
  height: number;
}

export interface FramingCamera {
  center: LngLat;
  zoom: number;
  rotationDeg: number;
  pitchDeg: number;
}

export const MERCATOR_WORLD_SIZE = 2 * Math.PI * 6378137;
export const TILE_SIZE_PX = 512;
export const FRAMING_REFERENCE_VIEWPORT: Viewport = { width: 1280, height: 800 };
export const FRAMING_FIT_PADDING = 1.1;
export const FRAMING_MAX_PITCH_DEG = 60;
export const FRAMING_MAX_ZOOM = 24;
export const FRAMING_MAX_ZOOM_FACTORS = [2, 4, 8, 16] as const;
export const FRAMING_MARGIN_RATIOS: Record<FramingMargin, number> = { tight: 1 / 6, normal: 1 / 2, wide: 1 };

const CIRCLE_EXTENT_POINTS = 32;

export const DEFAULT_FRAMING: MapFraming = {
  start: { mode: "content" },
  rotationDeg: 0,
  pitchDeg: 0,
  limit: { mode: "content", margin: "normal" },
  maxZoomFactor: 8,
};

function axes(rotationDeg: number): { right: MercatorPoint; up: MercatorPoint } {
  const bearing = (-rotationDeg * Math.PI) / 180;
  return {
    right: [Math.cos(bearing), -Math.sin(bearing)],
    up: [Math.sin(bearing), Math.cos(bearing)],
  };
}

const dot = (a: MercatorPoint, b: MercatorPoint) => a[0] * b[0] + a[1] * b[1];

export function metersPerPixelAtZoom(zoom: number): number {
  return MERCATOR_WORLD_SIZE / (TILE_SIZE_PX * 2 ** zoom);
}

export function zoomForMetersPerPixel(metersPerPixel: number): number {
  return Math.log2(MERCATOR_WORLD_SIZE / (TILE_SIZE_PX * metersPerPixel));
}

const extentPointsCache = new WeakMap<Shape, MercatorPoint[]>();

function shapeExtentPoints(shape: Shape): MercatorPoint[] {
  const cached = extentPointsCache.get(shape);
  if (cached) return cached;

  const coordinates = shape.coordinates.map(lngLatToMercator);
  let points = coordinates;

  if (shape.shapeKind === "circle" && coordinates.length >= 2) {
    const [center, edge] = coordinates as [MercatorPoint, MercatorPoint];
    const radius = Math.hypot(edge[0] - center[0], edge[1] - center[1]);
    points = [];
    for (let i = 0; i < CIRCLE_EXTENT_POINTS; i++) {
      const angle = (2 * Math.PI * i) / CIRCLE_EXTENT_POINTS;
      points.push([center[0] + radius * Math.cos(angle), center[1] + radius * Math.sin(angle)]);
    }
  }

  extentPointsCache.set(shape, points);
  return points;
}

export function shapesExtentPoints(shapes: Shape[]): MercatorPoint[] {
  return shapes.length === 1 ? shapeExtentPoints(shapes[0]!) : shapes.flatMap(shapeExtentPoints);
}

export function rectAroundPoints(points: MercatorPoint[], rotationDeg: number): FramingRect | null {
  if (!points.length) return null;

  const { right, up } = axes(rotationDeg);
  let minA = Infinity;
  let maxA = -Infinity;
  let minB = Infinity;
  let maxB = -Infinity;

  for (const point of points) {
    const a = dot(point, right);
    const b = dot(point, up);
    minA = Math.min(minA, a);
    maxA = Math.max(maxA, a);
    minB = Math.min(minB, b);
    maxB = Math.max(maxB, b);
  }

  const midA = (minA + maxA) / 2;
  const midB = (minB + maxB) / 2;

  return {
    center: [right[0] * midA + up[0] * midB, right[1] * midA + up[1] * midB],
    width: maxA - minA,
    height: maxB - minB,
  };
}

export function rectCorners(rect: FramingRect, rotationDeg: number): MercatorPoint[] {
  const { right, up } = axes(rotationDeg);
  const halfW = rect.width / 2;
  const halfH = rect.height / 2;
  const corner = (sa: number, sb: number): MercatorPoint => [
    rect.center[0] + right[0] * halfW * sa + up[0] * halfH * sb,
    rect.center[1] + right[1] * halfW * sa + up[1] * halfH * sb,
  ];

  return [corner(-1, 1), corner(1, 1), corner(1, -1), corner(-1, -1)];
}

function isUsableRect(rect: FramingRect | null): rect is FramingRect {
  return !!rect && rect.width > 0 && rect.height > 0;
}

export function resolveStartRect(framing: MapFraming, contentPoints: MercatorPoint[]): FramingRect | null {
  const rect = framing.start.mode === "frame"
    ? framing.start.frame
    : rectAroundPoints(contentPoints, framing.rotationDeg);

  return isUsableRect(rect) ? rect : null;
}

export function fitZoom(rect: FramingRect, viewport: Viewport): number {
  const metersPerPixel = Math.max(
    (rect.width * FRAMING_FIT_PADDING) / viewport.width,
    (rect.height * FRAMING_FIT_PADDING) / viewport.height,
  );

  return zoomForMetersPerPixel(metersPerPixel);
}

/** Zoom en dessous duquel l'écran déborderait de `rect` : à ce zoom, `rect` le recouvre tout juste. */
export function coverZoom(rect: FramingRect, viewport: Viewport): number {
  return zoomForMetersPerPixel(Math.min(rect.width / viewport.width, rect.height / viewport.height));
}

/** Angle de vue vertical de la caméra MapLibre par défaut (`Transform._fov`, 0,6435 rad). */
export const CAMERA_FOV_DEG = 36.87;

/** Au-delà, un coin d'écran viserait l'horizon : son point au sol partirait à l'infini. */
const MAX_GROUND_RAY_DEG = 89;

export interface LimitedView {
  center: MercatorPoint;
  zoom: number;
  rotationDeg: number;
  pitchDeg?: number;
}

/**
 * Encombrement au sol de l'écran, dans les axes de la limite, en pixels au centre de l'écran (à
 * multiplier par les mètres par pixel du zoom) et relativement au centre de la vue. À plat, c'est
 * l'écran lui-même, centré ; incliné, c'est un trapèze qui s'étire vers le haut de l'écran, d'où
 * des bornes dissymétriques.
 */
function groundFootprint(limitRotationDeg: number, view: LimitedView, viewport: Viewport, fovDeg: number) {
  const pitch = (Math.min(FRAMING_MAX_PITCH_DEG, Math.max(0, view.pitchDeg ?? 0)) * Math.PI) / 180;
  const distance = viewport.height / 2 / Math.tan((fovDeg * Math.PI) / 360);
  const screen = axes(view.rotationDeg);
  const limit = axes(limitRotationDeg);
  let minA = Infinity;
  let maxA = -Infinity;
  let minB = Infinity;
  let maxB = -Infinity;

  for (const [sx, sy] of [[-1, 1], [1, 1], [1, -1], [-1, -1]] as const) {
    const ray = Math.atan((sy * viewport.height) / 2 / distance);
    const tilt = Math.min(pitch + ray, (MAX_GROUND_RAY_DEG * Math.PI) / 180);
    const forward = distance * Math.cos(pitch) * Math.tan(tilt) - distance * Math.sin(pitch);
    const lateral = ((sx * viewport.width) / 2) * (Math.cos(ray) * Math.cos(pitch)) / Math.cos(tilt);
    const point: MercatorPoint = [
      screen.right[0] * lateral + screen.up[0] * forward,
      screen.right[1] * lateral + screen.up[1] * forward,
    ];
    const a = dot(point, limit.right);
    const b = dot(point, limit.up);
    minA = Math.min(minA, a);
    maxA = Math.max(maxA, a);
    minB = Math.min(minB, b);
    maxB = Math.max(maxB, b);
  }

  return { minA, maxA, minB, maxB };
}

/** Zoom en dessous duquel cette vue (orientation, inclinaison) laisserait voir au-delà de `limit`. */
export function limitZoomFloor(
  limit: FramingRect,
  limitRotationDeg: number,
  view: Omit<LimitedView, "center" | "zoom">,
  viewport: Viewport,
  fovDeg = CAMERA_FOV_DEG,
): number {
  const { minA, maxA, minB, maxB } = groundFootprint(limitRotationDeg, { ...view, center: [0, 0], zoom: 0 }, viewport, fovDeg);

  return zoomForMetersPerPixel(Math.min(limit.width / (maxA - minA), limit.height / (maxB - minB)));
}

/**
 * Ramène une vue dans la limite : zoomée juste assez pour que ce que montre l'écran ne déborde
 * jamais de `limit`, puis recentrée pour qu'il n'en sorte pas. Tient compte de l'écart d'orientation
 * entre la vue et la limite, et de l'inclinaison : l'écran est remplacé par ce qu'il voit au sol.
 */
export function constrainViewToLimit(
  limit: FramingRect,
  limitRotationDeg: number,
  view: LimitedView,
  viewport: Viewport,
  fovDeg = CAMERA_FOV_DEG,
): { center: MercatorPoint; zoom: number } {
  const footprint = groundFootprint(limitRotationDeg, view, viewport, fovDeg);
  const zoom = Math.max(
    view.zoom,
    zoomForMetersPerPixel(Math.min(
      limit.width / (footprint.maxA - footprint.minA),
      limit.height / (footprint.maxB - footprint.minB),
    )),
  );
  const metersPerPixel = metersPerPixelAtZoom(zoom);
  const { right, up } = axes(limitRotationDeg);
  const offset: MercatorPoint = [view.center[0] - limit.center[0], view.center[1] - limit.center[1]];
  // Le centre peut aller tant que l'encombrement reste dans la limite ; au zoom plancher, il n'a
  // plus qu'une position possible (bornes confondues, à l'arrondi près).
  const clamp = (value: number, low: number, high: number) =>
    low > high ? (low + high) / 2 : Math.min(high, Math.max(low, value));
  const a = clamp(
    dot(offset, right),
    -limit.width / 2 - footprint.minA * metersPerPixel,
    limit.width / 2 - footprint.maxA * metersPerPixel,
  );
  const b = clamp(
    dot(offset, up),
    -limit.height / 2 - footprint.minB * metersPerPixel,
    limit.height / 2 - footprint.maxB * metersPerPixel,
  );

  return {
    center: [limit.center[0] + right[0] * a + up[0] * b, limit.center[1] + right[1] * a + up[1] * b],
    zoom,
  };
}

export function resolveLimitRect(
  framing: MapFraming,
  contentPoints: MercatorPoint[],
  startRect: FramingRect | null,
): FramingRect | null {
  let rect: FramingRect | null = null;

  if (framing.limit.mode === "frame") rect = framing.limit.frame;

  if (framing.limit.mode === "content") {
    const startCorners = startRect ? rectCorners(startRect, framing.rotationDeg) : [];
    const content = rectAroundPoints([...contentPoints, ...startCorners], framing.rotationDeg);

    if (content) {
      const margin = Math.max(content.width, content.height) * FRAMING_MARGIN_RATIOS[framing.limit.margin];
      rect = { center: content.center, width: content.width + 2 * margin, height: content.height + 2 * margin };
    }
  }

  return isUsableRect(rect) ? rect : null;
}

export function resolveMaxZoom(framing: MapFraming, startRect: FramingRect | null): number | null {
  if (!startRect) return null;

  const zoom = fitZoom(startRect, FRAMING_REFERENCE_VIEWPORT) + Math.log2(Math.max(1, framing.maxZoomFactor));

  return Math.min(FRAMING_MAX_ZOOM, zoom);
}

export function maxZoomFactorForZoom(zoom: number, startRect: FramingRect): number {
  const factor = 2 ** (zoom - fitZoom(startRect, FRAMING_REFERENCE_VIEWPORT));

  return Math.max(1, Math.round(factor * 10) / 10);
}

/**
 * Caméra d'arrivée : la vue de départ cadrée sur `viewport`, sauf si l'écran déborderait alors de
 * `limitRect` — la limite l'emporte, la vue est zoomée et recentrée juste assez pour y rester.
 */
export function resolveStartCamera(
  framing: MapFraming,
  startRect: FramingRect,
  viewport: Viewport,
  limitRect: FramingRect | null = null,
): FramingCamera {
  const fitted = { center: startRect.center, zoom: fitZoom(startRect, viewport) };
  const { center, zoom } = limitRect
    ? constrainViewToLimit(limitRect, framing.rotationDeg, { ...fitted, rotationDeg: framing.rotationDeg }, viewport)
    : fitted;

  return {
    center: mercatorToLngLat(center),
    zoom,
    rotationDeg: framing.rotationDeg,
    pitchDeg: framing.pitchDeg,
  };
}

export function frameFromCamera(center: LngLat, zoom: number, viewport: Viewport = FRAMING_REFERENCE_VIEWPORT): FramingRect {
  const metersPerPixel = metersPerPixelAtZoom(zoom);

  return {
    center: lngLatToMercator(center),
    width: (viewport.width * metersPerPixel) / FRAMING_FIT_PADDING,
    height: (viewport.height * metersPerPixel) / FRAMING_FIT_PADDING,
  };
}

export function framingFromLegacyView(view: {
  center: LngLat;
  zoom: number;
  zoomMax?: number;
  rotationDeg: number;
}): MapFraming {
  const factor = view.zoomMax != null ? 2 ** (view.zoomMax - view.zoom) : DEFAULT_FRAMING.maxZoomFactor;

  return {
    start: { mode: "frame", frame: frameFromCamera(view.center, view.zoom) },
    rotationDeg: view.rotationDeg,
    pitchDeg: 0,
    limit: { mode: "none" },
    maxZoomFactor: Math.min(64, Math.max(1, Math.round(factor * 10) / 10)),
  };
}

const isFiniteNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

function normalizePoint(value: unknown): MercatorPoint | null {
  return Array.isArray(value) && value.length === 2 && isFiniteNumber(value[0]) && isFiniteNumber(value[1])
    ? [value[0], value[1]]
    : null;
}

export function normalizeFraming(value: unknown): MapFraming | undefined {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return undefined;

  const raw = value as Record<string, unknown>;
  const rawStart = (raw.start ?? {}) as Record<string, unknown>;
  const rawLimit = (raw.limit ?? {}) as Record<string, unknown>;

  let start: FramingStart = DEFAULT_FRAMING.start;
  const startCenter = normalizePoint(rawStart.center);
  if (rawStart.mode === "frame" && startCenter && isFiniteNumber(rawStart.width) && isFiniteNumber(rawStart.height)
    && rawStart.width > 0 && rawStart.height > 0) {
    start = { mode: "frame", frame: { center: startCenter, width: rawStart.width, height: rawStart.height } };
  }

  let limit: FramingLimit = DEFAULT_FRAMING.limit;
  const limitCenter = normalizePoint(rawLimit.center);
  if (rawLimit.mode === "none") limit = { mode: "none" };
  if (rawLimit.mode === "content" && typeof rawLimit.margin === "string" && rawLimit.margin in FRAMING_MARGIN_RATIOS) {
    limit = { mode: "content", margin: rawLimit.margin as FramingMargin };
  }
  if (rawLimit.mode === "frame" && limitCenter && isFiniteNumber(rawLimit.width) && isFiniteNumber(rawLimit.height)
    && rawLimit.width > 0 && rawLimit.height > 0) {
    limit = { mode: "frame", frame: { center: limitCenter, width: rawLimit.width, height: rawLimit.height } };
  }

  return {
    start,
    rotationDeg: isFiniteNumber(raw.rotation) ? raw.rotation : 0,
    pitchDeg: isFiniteNumber(raw.pitch) ? Math.min(FRAMING_MAX_PITCH_DEG, Math.max(0, raw.pitch)) : 0,
    limit,
    maxZoomFactor: isFiniteNumber(raw.maxZoomFactor) && raw.maxZoomFactor >= 1
      ? raw.maxZoomFactor
      : DEFAULT_FRAMING.maxZoomFactor,
  };
}

export function serializeFraming(framing: MapFraming): Record<string, unknown> {
  return {
    start: framing.start.mode === "frame"
      ? { mode: "frame", center: framing.start.frame.center, width: framing.start.frame.width, height: framing.start.frame.height }
      : { mode: "content" },
    rotation: framing.rotationDeg,
    pitch: framing.pitchDeg,
    limit: framing.limit.mode === "frame"
      ? { mode: "frame", center: framing.limit.frame.center, width: framing.limit.frame.width, height: framing.limit.frame.height }
      : framing.limit,
    maxZoomFactor: framing.maxZoomFactor,
  };
}

export function resolveFramingGeometry(framing: MapFraming, contentPoints: MercatorPoint[]): FramingGeometry {
  const startRect = resolveStartRect(framing, contentPoints);
  const limitRect = resolveLimitRect(framing, contentPoints, startRect);
  const toLngLat = (points: MercatorPoint[]) => points.map((point) => mercatorToLngLat(point));
  const maxZoom = resolveMaxZoom(framing, startRect);
  const minZoom = limitRect ? coverZoom(limitRect, FRAMING_REFERENCE_VIEWPORT) : null;

  return {
    startRect,
    limitRect,
    start: startRect ? toLngLat(rectCorners(startRect, framing.rotationDeg)) : null,
    limit: limitRect ? toLngLat(rectCorners(limitRect, framing.rotationDeg)) : null,
    maxZoom,
    minZoom: minZoom === null ? null : Math.min(minZoom, maxZoom ?? FRAMING_MAX_ZOOM),
  };
}
